<template>
  <div
    class="modal"
    @mousedown="closeModal"
  >
    <div
      class="sprint-modal-wrap"
      @mousedown.stop
    >
      <PopupHeader
        :title="headerTitle"
        @close="closeModal"
      />
      <div class="modal-body px-6">
        <div class="text-sm text-text-textStrong mt-2 mb-0.5">{{ bodyDescription }}</div>
        <StInput
          v-if="modalType === 'rename'"
          :key="'sprint-tittle'"
          :value="sprintName"
          :text-value="sprintName"
          :placeholder-text="translate('enterSprintName')"
          label-text=""
          :input-type="'text'"
          :input-height="36"
          :focus-field="true"
          @input="sprintName = $event"
          @enter-key="submit"
        />
        <div
          v-if="modalType === 'changeDates'"
          class="flex items-center mb-6 mt-1.5"
        >
          <v-popover
            interactive
            tag="div"
            content-tag="div"
            placement="bottom"
            :role="'poper'"
            :trigger="'click'"
            :append-to="bodyRef"
          >
            <div class="sprint-date-placeholder">
              <em
                class="custom-icons bg-icon-default"
                :style="getCustomIconStyle(iconPath('calendar'))"
              />
              <span class="font-normal text-sm text-text-default ml-2">{{
                sprintStartDateToString
              }}</span>
            </div>
            <template #content="{ state, hide }">
              <DateTimePicker
                v-if="state.isVisible"
                :property="'start_date'"
                :selected-date="startDate"
                :disable-delete="true"
                @due-date="setStartDate($event, hide)"
                @esc="hide"
              />
            </template>
          </v-popover>
          <span class="font-normal text-sm text-text-default mx-2">{{ translate('to') }}</span>
          <v-popover
            interactive
            tag="div"
            content-tag="div"
            placement="bottom"
            :role="'poper'"
            :trigger="'click'"
            :append-to="bodyRef"
          >
            <div class="sprint-date-placeholder">
              <em
                class="custom-icons bg-icon-default"
                :style="getCustomIconStyle(iconPath('calendar'))"
              />
              <span class="font-normal text-sm text-text-default ml-2">{{
                sprintEndDateToString
              }}</span>
            </div>
            <template #content="{ state, hide }">
              <DateTimePicker
                v-if="state.isVisible"
                :property="'due_date'"
                :selected-date="endDate"
                :disable-delete="true"
                @due-date="setEndDate($event, hide)"
                @esc="hide"
              />
            </template>
          </v-popover>
        </div>
      </div>
      <div class="flex items-center justify-between px-6 pb-6">
        <ButtonCustom
          class="text-sm font-normal rounded"
          :text="translate('cancel')"
          :type="'popup-button-wrap important-custom'"
          :button-dimensions="{ width: 173, height: 32 }"
          @click="closeModal"
        />
        <ButtonCustom
          class="text-sm font-normal rounded"
          :text="submitButtonText"
          :type="'popup-button-wrap attention'"
          :button-dimensions="{ width: 173, height: 32 }"
          :disabled="disableSubmit"
          @click="submit"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance, defineAsyncComponent, onMounted, watch } from 'vue';
import ButtonCustom from '@/components/elements/ButtonCustom';
import StInput from '@/components/elements/StInput';
import useTranslate from '@/utilities/composables/useTranslate';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import { convertTimestampToDateSecondFormat } from '@/utilities/timeDifference';
import PopupHeader from '@/components/elements/PopupHeader';
import format from 'date-fns/format';

const DateTimePicker = defineAsyncComponent(
  () => import(/* webpackChunkName: "sprintModal" */ '@/components/widgets/DateTimePicker')
);

const { proxy } = getCurrentInstance() as any;
const _this = proxy;

const { translate } = useTranslate();
const { iconPath, getCustomIconStyle } = useCustomIcon();

const sprintName = ref('');
const startDate = ref();
const endDate = ref();

onMounted(() => {
  sprintName.value = sprint.value?.title || '';
  startDate.value = sprint.value?.start_date || 0;
  endDate.value = sprint.value?.end_date || 0;
});

const projectId = computed(() => _this.$store.getters.getCurrentProjectId);

const headerTitle = computed(() => {
  return modalType.value === 'changeDates'
    ? translate('changeSprintDatesFor', { sprintName: sprint.value.title })
    : translate('renameSprint');
});

const submitButtonText = computed(() => {
  return translate(modalType.value);
});

const bodyDescription = computed(() => {
  return modalType.value === 'changeDates'
    ? translate('chooseNewStartAndEndDates', { sprintName: sprint.value.title })
    : translate('renameSprintDescription');
});

const sprintForEdit = computed(() => {
  return _this.$store.getters.getSprintForEditId;
});

const modalType = computed(() => {
  return sprintForEdit.value?.action;
});

const sprint = computed(() => {
  return _this.$store.getters.getSpaceSprints(projectId.value)[sprintForEdit.value?.id] || {};
});

const sprintStartDateToString = computed(() => {
  return `${getWeekDate(startDate.value)} ${convertTimestampToDateSecondFormat(startDate.value)}`;
});

const sprintEndDateToString = computed(() => {
  return `${getWeekDate(endDate.value)} ${convertTimestampToDateSecondFormat(endDate.value)}`;
});

const getWeekDate = (date: any) => {
  if (!date) return '';
  return format(new Date(date * 1000), 'E', {
    weekStartsOn: 1,
  });
};

const disableSubmit = computed(() => {
  return (
    ((sprintName.value === '' || sprintName.value === sprint.value.title) &&
      modalType.value === 'rename') ||
    ((!startDate.value ||
      !endDate.value ||
      (startDate.value === sprint.value.start_date && endDate.value === sprint.value.end_date) ||
      new Date(startDate.value * 1000) >= new Date(endDate.value * 1000)) &&
      modalType.value === 'changeDates')
  );
});

const updateSprint = (payload: any, oldSprint: any) => {
  return _this.$store
    .dispatch('updateSprint', {
      projectId: projectId.value,
      sprintId: sprintForEdit.value.orginalId,
      payload,
    })
    .then(() => {
      closeModal();
    })
    .catch((err: any) => {
      if (err.response.status === 400) {
        _this.$store.dispatch('addUiNotification', {
          message: translate('startDateIsEarlierThanPreviousEndDate'),
          status: _this.$constants.uiNotificationStatuses.error,
          duration: 4000,
        });
      }
      localUpdateSprint('title', oldSprint.title);
      localUpdateSprint('start_date', oldSprint.start_date);
      localUpdateSprint('end_date', oldSprint.end_date);
    });
};

const localUpdateSprint = (prop: string, value: any) => {
  _this.$store.dispatch('localUpdateSprint', {
    projectId: projectId.value,
    sprintId: sprint.value.id,
    prop,
    value,
  });
};

const submit = () => {
  const oldSprint = { ...sprint.value };

  if (modalType.value === 'rename') {
    localUpdateSprint('title', sprintName.value);
    updateSprint({ title: sprintName.value }, oldSprint);
  }
  if (modalType.value === 'changeDates') {
    localUpdateSprint('start_date', startDate.value);
    localUpdateSprint('end_date', endDate.value);
    updateSprint(
      { start_date: sprint.value.start_date, end_date: sprint.value.end_date },
      oldSprint
    );
  }
};

const closeModal = () => {
  _this.$store.dispatch('setSprintForEdit', {});
};

const setStartDate = (date: number, hide?: Function) => {
  hide?.();
  startDate.value = date;
};

const setEndDate = (date: string, hide?: Function) => {
  hide?.();
  endDate.value = date;
};

const currentRoute = computed(() => _this.$route.path);

const bodyRef = ref(document.body);
watch(currentRoute, () => closeModal());
</script>

<style lang="scss">
.sprint-modal-wrap {
  @apply flex flex-col w-[400px] border border-solid border-important-border rounded-lg bg-background-strongest;

  .modal-body .input-wrap {
    @apply h-[64px];
  }

  .sprint-date-placeholder {
    @apply flex items-center border border-solid border-standard-border rounded px-2.5 py-[7px] bg-standard-bg cursor-pointer;
  }
}
</style>
